import type { AppStore } from '../core/store';
import type { WorkspaceLayout } from '../core/types';
import { icon } from './icons';
import {
  ATTRIBUTE_TABLE_MAX, ATTRIBUTE_TABLE_MIN, defaultWorkspaceLayout, effectiveSidePanels, effectiveTable,
  LEFT_PANEL_MAX, LEFT_PANEL_MIN, MIN_MAP_WIDTH, normalizeWorkspaceLayout, RIGHT_PANEL_MAX, RIGHT_PANEL_MIN,
  saveWorkspaceLayoutPreference,
} from './workspaceLayout';

type ResizeTarget = 'left' | 'right' | 'bottom';
interface DragState { readonly target: ResizeTarget; readonly pointerId: number; readonly startCoordinate: number; readonly startSize: number; readonly handle: HTMLElement }

const required = <T extends Element = HTMLElement>(selector: string): T => { const element = document.querySelector<T>(selector); if (!element) throw new Error(`No se encontró ${selector}`); return element; };
const clamp = (value: number, minimum: number, maximum: number): number => Math.max(minimum, Math.min(value, maximum));

export class WorkspaceLayoutController {
  private drag: DragState | null = null;
  private frame: number | null = null;
  private readonly shell = required<HTMLElement>('.app-shell');
  private readonly workspace = required<HTMLElement>('.workspace');

  constructor(private readonly store: AppStore, private readonly updateMapSize: () => void) {
    document.querySelectorAll<HTMLElement>('[data-panel-resize]').forEach((handle) => {
      handle.addEventListener('pointerdown', (event) => this.startResize(event, handle.dataset.panelResize as ResizeTarget, handle));
      handle.addEventListener('dblclick', () => this.resetSize(handle.dataset.panelResize as ResizeTarget));
    });
    required('#toggle-left-panel').addEventListener('click', () => this.toggleSide('left'));
    required('#toggle-inspector').addEventListener('click', () => this.toggleSide('right'));
    required('#toggle-table').addEventListener('click', () => this.patch({ attributeTableOpen: !this.store.getState().workspaceLayout.attributeTableOpen }));
    document.querySelectorAll<HTMLElement>('[data-panel-collapse]').forEach((button) => button.addEventListener('click', () => this.patch(button.dataset.panelCollapse === 'left' ? { leftPanelCollapsed: true } : { rightPanelCollapsed: true })));
    // Compact ⇄ expanded keeps the table docked; closing removes it. Neither touches the selection or the data.
    required('#table-mode').addEventListener('click', () => this.patch({ attributeTableMode: this.tableState() === 'compact' ? 'expanded' : 'compact' }));
    required('#close-attribute-table').addEventListener('click', () => this.patch({ attributeTableOpen: false }));
    required('#open-attribute-table').addEventListener('click', () => this.openTable());
    document.querySelector('#reset-layout')?.addEventListener('click', () => this.resetLayout());
    // Mobile drawers are opened/closed by ShellController through data-mobile-dock: the toggles mirror them.
    new MutationObserver(() => this.render(this.store.getState().workspaceLayout)).observe(this.shell, { attributes: true, attributeFilter: ['data-mobile-dock'] });
    document.addEventListener('pointermove', (event) => this.resize(event));
    document.addEventListener('pointerup', (event) => this.finishResize(event));
    document.addEventListener('pointercancel', (event) => this.finishResize(event));
    window.addEventListener('resize', () => { this.render(this.store.getState().workspaceLayout); this.scheduleMapResize(); });
    this.store.subscribe((state, previous) => {
      if (state.workspaceLayout === previous.workspaceLayout) return;
      saveWorkspaceLayoutPreference(state.workspaceLayout); this.render(state.workspaceLayout); this.scheduleMapResize();
      window.setTimeout(this.updateMapSize, 220);
    });
    this.render(this.store.getState().workspaceLayout);
  }

  /** Opens the table expanded (from «Ver en tabla», a layer's table action or its title) without other side effects. */
  openTable(): void {
    if (this.tableState() !== 'expanded') this.patch({ attributeTableOpen: true, attributeTableMode: 'expanded' });
    window.requestAnimationFrame(() => required<HTMLElement>('#attribute-table').focus({ preventScroll: true }));
  }

  /** «Restablecer disposición»: panel sizes and visibility only; layers, data, selection and geometry are untouched. */
  resetLayout(): void {
    delete this.shell.dataset.mobileDock;
    this.store.update({ workspaceLayout: normalizeWorkspaceLayout(defaultWorkspaceLayout) }, { markDirty: false });
  }

  render(layoutValue: WorkspaceLayout): void {
    const layout = normalizeWorkspaceLayout(layoutValue);
    const effective = effectiveSidePanels(layout, this.workspace.getBoundingClientRect().width || window.innerWidth);
    const table = effectiveTable(layout, window.innerHeight);
    this.workspace.style.setProperty('--left-panel-width', `${effective.leftWidth}px`);
    this.workspace.style.setProperty('--right-panel-width', `${effective.rightWidth}px`);
    this.shell.style.setProperty('--attribute-table-height', `${table.height || layout.attributeTableHeight}px`);
    this.workspace.classList.toggle('left-panel-collapsed', layout.leftPanelCollapsed || effective.autoCollapseLeft);
    this.workspace.classList.toggle('inspector-collapsed', layout.rightPanelCollapsed || effective.autoCollapseRight);
    this.workspace.dataset.autoCollapsedLeft = String(effective.autoCollapseLeft);
    this.workspace.dataset.autoCollapsedRight = String(effective.autoCollapseRight);
    this.shell.classList.toggle('table-open', layout.attributeTableOpen);
    this.shell.classList.toggle('table-compact', table.state === 'compact');
    const drawer = required('#attribute-drawer');
    drawer.classList.toggle('open', layout.attributeTableOpen);
    drawer.dataset.tableState = table.state;
    drawer.dataset.autoCompact = String(table.autoCompact);
    const mode = required<HTMLButtonElement>('#table-mode');
    const expandTitle = table.autoCompact ? 'La ventana es baja: la tabla se muestra compacta para conservar el mapa' : 'Expandir tabla';
    mode.innerHTML = icon(table.state === 'compact' ? 'chevronUp' : 'chevron');
    mode.title = table.state === 'compact' ? expandTitle : 'Contraer tabla (deja solo el encabezado)';
    mode.setAttribute('aria-label', table.state === 'compact' ? 'Expandir tabla' : 'Contraer tabla');
    mode.setAttribute('aria-expanded', String(table.state === 'expanded'));
    mode.disabled = table.autoCompact;
    // aria-pressed mirrors what is actually on screen: a panel auto-collapsed by a narrow window reads as off;
    // on a phone the side panels are drawers, so their toggles mirror the open drawer.
    const compactScreen = this.isCompactScreen(); const drawerOpen = this.shell.dataset.mobileDock;
    const leftVisible = compactScreen ? drawerOpen === 'left' : !(layout.leftPanelCollapsed || effective.autoCollapseLeft);
    const rightVisible = compactScreen ? drawerOpen === 'right' : !(layout.rightPanelCollapsed || effective.autoCollapseRight);
    this.renderToggle('#toggle-left-panel', leftVisible, leftVisible ? 'Ocultar panel de capas' : 'Mostrar panel de capas');
    this.renderToggle('#toggle-inspector', rightVisible, rightVisible ? 'Ocultar panel de propiedades' : 'Mostrar panel de propiedades');
    const tableToggle = this.renderToggle('#toggle-table', layout.attributeTableOpen, layout.attributeTableOpen ? 'Ocultar tabla de atributos' : 'Mostrar tabla de atributos');
    tableToggle.setAttribute('aria-expanded', String(layout.attributeTableOpen));
  }

  private tableState(): 'closed' | 'compact' | 'expanded' { return effectiveTable(this.store.getState().workspaceLayout, window.innerHeight).state; }

  private isCompactScreen(): boolean { return window.matchMedia?.('(max-width: 760px)').matches === true; }

  /** Desktop: collapse/restore the docked panel. Phone: open/close its drawer (only one drawer at a time). */
  private toggleSide(side: 'left' | 'right'): void {
    if (this.isCompactScreen()) {
      if (this.shell.dataset.mobileDock === side) delete this.shell.dataset.mobileDock; else this.shell.dataset.mobileDock = side;
      return;
    }
    const layout = this.store.getState().workspaceLayout;
    const width = this.workspace.getBoundingClientRect().width || window.innerWidth;
    const effective = effectiveSidePanels(layout, width);
    const visible = side === 'left' ? !(layout.leftPanelCollapsed || effective.autoCollapseLeft) : !(layout.rightPanelCollapsed || effective.autoCollapseRight);
    if (visible) { this.patch(side === 'left' ? { leftPanelCollapsed: true } : { rightPanelCollapsed: true }); return; }
    // Showing a panel the window had no room for hides the other side instead of doing nothing.
    const shown = side === 'left' ? { ...layout, leftPanelCollapsed: false } : { ...layout, rightPanelCollapsed: false };
    const after = effectiveSidePanels(shown, width);
    const stillHidden = side === 'left' ? after.autoCollapseLeft : after.autoCollapseRight;
    this.patch(side === 'left' ? { leftPanelCollapsed: false, ...(stillHidden ? { rightPanelCollapsed: true } : {}) } : { rightPanelCollapsed: false, ...(stillHidden ? { leftPanelCollapsed: true } : {}) });
  }

  private renderToggle(selector: string, pressed: boolean, title: string): HTMLButtonElement {
    const button = required<HTMLButtonElement>(selector);
    button.title = title; button.setAttribute('aria-label', title); button.setAttribute('aria-pressed', String(pressed));
    return button;
  }

  private patch(patch: Partial<WorkspaceLayout>): void {
    const current = this.store.getState().workspaceLayout;
    this.store.update({ workspaceLayout: normalizeWorkspaceLayout({ ...current, ...patch }) });
  }

  private startResize(event: PointerEvent, target: ResizeTarget, handle: HTMLElement): void {
    if (event.button !== 0) return;
    const layout = this.store.getState().workspaceLayout;
    const startSize = target === 'left' ? layout.leftPanelWidth : target === 'right' ? layout.rightPanelWidth : layout.attributeTableHeight;
    if ((target === 'left' && layout.leftPanelCollapsed) || (target === 'right' && layout.rightPanelCollapsed) || (target === 'bottom' && this.tableState() !== 'expanded')) return;
    this.drag = { target, pointerId: event.pointerId, startCoordinate: target === 'bottom' ? event.clientY : event.clientX, startSize, handle };
    handle.classList.add('active'); document.body.classList.add('workspace-resizing'); event.preventDefault(); event.stopPropagation();
  }

  private resize(event: PointerEvent): void {
    if (!this.drag || event.pointerId !== this.drag.pointerId) return;
    const coordinate = this.drag.target === 'bottom' ? event.clientY : event.clientX;
    const direction = this.drag.target === 'left' ? 1 : -1;
    const raw = this.drag.startSize + (coordinate - this.drag.startCoordinate) * direction;
    const size = this.limitSize(this.drag.target, raw);
    if (this.drag.target === 'left') this.workspace.style.setProperty('--left-panel-width', `${size}px`);
    else if (this.drag.target === 'right') this.workspace.style.setProperty('--right-panel-width', `${size}px`);
    else this.shell.style.setProperty('--attribute-table-height', `${effectiveTable({ ...this.store.getState().workspaceLayout, attributeTableHeight: size }, window.innerHeight).height}px`);
    this.scheduleMapResize(); event.preventDefault(); event.stopPropagation();
  }

  private finishResize(event: PointerEvent): void {
    if (!this.drag || event.pointerId !== this.drag.pointerId) return;
    const coordinate = this.drag.target === 'bottom' ? event.clientY : event.clientX;
    const direction = this.drag.target === 'left' ? 1 : -1;
    const size = this.limitSize(this.drag.target, this.drag.startSize + (coordinate - this.drag.startCoordinate) * direction);
    if (this.drag.target === 'left') this.patch({ leftPanelWidth: size });
    else if (this.drag.target === 'right') this.patch({ rightPanelWidth: size });
    else this.patch({ attributeTableHeight: size });
    this.drag.handle.classList.remove('active'); document.body.classList.remove('workspace-resizing'); this.drag = null;
  }

  private limitSize(target: ResizeTarget, value: number): number {
    const width = this.workspace.getBoundingClientRect().width || window.innerWidth;
    const styles = getComputedStyle(this.workspace);
    if (target === 'left') {
      const right = Number.parseFloat(styles.getPropertyValue('--right-panel-width')) || this.store.getState().workspaceLayout.rightPanelWidth;
      return clamp(value, LEFT_PANEL_MIN, Math.max(LEFT_PANEL_MIN, Math.min(LEFT_PANEL_MAX, width - right - MIN_MAP_WIDTH - 2)));
    }
    if (target === 'right') {
      const left = Number.parseFloat(styles.getPropertyValue('--left-panel-width')) || this.store.getState().workspaceLayout.leftPanelWidth;
      return clamp(value, RIGHT_PANEL_MIN, Math.max(RIGHT_PANEL_MIN, Math.min(RIGHT_PANEL_MAX, width - left - MIN_MAP_WIDTH - 2)));
    }
    return clamp(value, ATTRIBUTE_TABLE_MIN, ATTRIBUTE_TABLE_MAX);
  }

  private resetSize(target: ResizeTarget): void {
    if (target === 'left') this.patch({ leftPanelWidth: defaultWorkspaceLayout.leftPanelWidth });
    else if (target === 'right') this.patch({ rightPanelWidth: defaultWorkspaceLayout.rightPanelWidth });
    else this.patch({ attributeTableHeight: defaultWorkspaceLayout.attributeTableHeight });
  }

  private scheduleMapResize(): void {
    if (this.frame !== null) return;
    this.frame = window.requestAnimationFrame(() => { this.frame = null; this.updateMapSize(); });
  }
}
